import React, { useEffect, useState } from 'react';
import axios, { HttpStatusCode } from 'axios';
import ContentSection from '../common/ContentSection/ContentSection';
import { useParams } from 'react-router-dom';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import LoadingPage from '../LoadingPage';
import { getApiClient } from '../../api';
import type { CouncilMember, CompareResponse } from '../../api';
import { ComparisonDetailMembersDataType } from '../../types';
import DirectMemberComparisons from './DirectMemberComparisons';
import { redirectErrorPage } from '../../utils/errors';
import MatchStatSection from './MatchStatSection';
import VotingComparison from './VotingComparison';
import MatchChart from './MatchChartSection';
import { Message } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';

type Props = {
    firstMemberId: number;
    secondMemberId: number;
};

const ComparisonDetail = ({ firstMemberId, secondMemberId }: Props) => {
    const { bodyType = '', zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ComparisonDetailMembersDataType>({
        firstMember: null,
        secondMember: null,
        comparison: null
    });
    const { t } = useTranslation(['analyses']);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);


    useEffect(() => {
        const fetchDataForBothMembers = async () => {
            setLoading(true);
            try {
                const api = getApiClient();
                const councilId = parseInt(zastupitelstvoId ?? '0', 10);
                const [firstMemberData, secondMemberData] = await Promise.all([
                    api.getMemberByMunicipalityIdAndBodyAndCouncilIdAndMemberId(municipalitaId, bodyType, councilId, firstMemberId),
                    api.getMemberByMunicipalityIdAndBodyAndCouncilIdAndMemberId(municipalitaId, bodyType, councilId, secondMemberId)
                ]);

                let comparisonData: CompareResponse | null = null;
                try {
                    comparisonData = await api.getCompareByMunicipalityIdAndBodyAndCouncilId(
                        municipalitaId, bodyType, councilId, { prvni_clen: firstMemberId, druhy_clen: secondMemberId }
                    );
                } catch (compareError) {
                    const status = axios.isAxiosError(compareError) ? compareError.response?.status : undefined;
                    if (status === HttpStatusCode.UnprocessableEntity) {
                        setErrorMessage(t('comparison.errorOverlayComparison'));
                    } else if (status === HttpStatusCode.NotFound) {
                        setErrorMessage(t('comparison.selectTwoDifferent'));
                    } else {
                        throw compareError;
                    }
                }

                setData({
                    comparison: comparisonData,
                    firstMember: firstMemberData as CouncilMember,
                    secondMember: secondMemberData as CouncilMember
                });
            } catch (error) {
                redirectErrorPage(error);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 333);
            }
        };

        fetchDataForBothMembers();
        // Including data in the dependency array would cause an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstMemberId, secondMemberId, municipalitaId, bodyType, zastupitelstvoId]);

    if (loading || data.firstMember === null || data.secondMember === null) {
        return <LoadingPage />;
    }

    if (data.comparison == null) {
        return (
            <>
                <ContentSection level={3} className='dashboard-card'>
                    <Message
                        severity="warn"
                        text={errorMessage}
                        style={{ margin: '0 auto' }}
                    />
                </ContentSection>
            </>
        );
    }
    return (
        <>
            <ContentSection level={3} className='dashboard-card'>
                <DirectMemberComparisons {...data} />
                <MatchStatSection
                    comparison={data.comparison}
                    dateFrom={data.comparison?.date_from ?? null}
                    dateTo={data.comparison?.date_to ?? null}
                />
            </ContentSection>
            <VotingComparison comparison={data.comparison} />
            <MatchChart comparison={data.comparison} />
        </>
    );
};

export default ComparisonDetail;
